import path from 'path'
import React from 'react'
import { Skeleton, Empty, Row, Col, Divider, Card, Modal, Button, Popover, Popconfirm, message, Result } from 'antd'
import { EditOutlined, DeleteOutlined, PrinterOutlined } from '@ant-design/icons'
import QRCode from 'qrcode.react'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { FormComponent } from '../../../../BaseComponent'
import { QrCodeContextData } from '../../../../../context/QrCodeContext'
import IQrCode from '../../../../../interfaces/IQrCode'
import IBranch from '../../../../../interfaces/IBranch'
import Config from '../../../../../config'
import PrintQrCodeProps from './Print'
import RegisterQrCode from './Register'

export type QrCodeProps = {
  shelterUuid: string,
  shelterBranches: IBranch[],

  context: QrCodeContextData,

  onPrintAll: (callback: any) => void
}

export default class QrCode extends FormComponent<QrCodeProps> {

  state = {
    loaded: false,
    showRegisterPopover: false,
    showEditPopover: [] as string[]
  }

  private load() {
    return this.props.context.requestQrCodeList({
      search: {
        branch: {
          with: {
            _exists: true,
            shelter: {
              with: {
                _exists: true,
                uuid: this.props.shelterUuid
              }
            }
          }
        }
      },
      options: {
        sort: ['created_date']
      }
    })

  }

  async componentDidMount() {
    await this.load()

    // Handle parent event :)
    this.props.onPrintAll(() => this.handlePrintAll())

    this.setState({
      loaded: true,
      showRegisterPopover: !this.props.context.qrCodes?.length
    }, () => {
      this.setState({
        showRegisterPopover: false
      })
    })
  }

  private handlePopoverVisibleChange = (visible: boolean, uuid?: string, shouldReload?: boolean) => {
    if (uuid) {
      let { showEditPopover } = this.state

      if (visible && !showEditPopover.includes(uuid)) {
        showEditPopover.push(uuid)
      }
      if (!visible) {
        showEditPopover = showEditPopover.filter(v => v !== uuid)
      }

      this.setState({
        showEditPopover: showEditPopover
      }, async () => {
        if (shouldReload) {
          await this.load()
        }
      })
    } else {
      this.setState({
        showRegisterPopover: visible
      }, async () => {
        if (shouldReload) {
          await this.load()
        }
      })
    }
  }

  private getQrCodeBase64 = (qrCode: IQrCode) => {
    const doc = document.getElementById(`qr-code-${qrCode.uuid}`)
    const qrCodeCanvas = doc?.querySelector('canvas')
    const qrCodeDataUri = qrCodeCanvas?.toDataURL('image/jpg', 0.3)

    return qrCodeDataUri || ''
  }

  private showPrintModal = (qrCodes: any[]) => {
    Modal.info({
      icon: null,
      closable: true,
      okButtonProps: {
        style: {
          display: 'none'
        }
      },
      content: (
        <PDFDownloadLink document={<PrintQrCodeProps qrCodes={qrCodes} />} fileName={this.props.shelterUuid}>
          {({ loading, url }) => {
            if (loading) return <Skeleton />

            return (
              <Result
                status="success"
                title="QR Code is ready for download!"
                extra={[
                  <Button
                    type="primary"
                    onClick={() => window.open(url || '', '_blank')}
                  >
                    Download PDF
                  </Button>
                ]}
              />
            )
          }}
        </PDFDownloadLink>
      )
    })
  }

  private handlePrintOne = (qrCode: IQrCode) => {
    qrCode = {
      ...qrCode,
      qrCodeDataUri: this.getQrCodeBase64(qrCode)
    } as any

    this.showPrintModal([qrCode])
  }

  private handlePrintAll = () => {
    const { context } = this.props

    const qrCodes: any[] = context.qrCodes?.map(v => {
      return {
        ...v,
        qrCodeDataUri: this.getQrCodeBase64(v)
      } as any
    }) || []

    this.showPrintModal(qrCodes)
  }

  private handleDelete = async (qrCode: IQrCode) => {
    const { context } = this.props

    try {
      await context.requestQrCodeDelete({
        qr_code_uuid: qrCode.uuid
      })
      await this.load()

      message.success('QR Code deleted successfully!')
    } catch (error) {
      message.error(`Error to delete QR Code. Reason: ${error}`)
    }
  }

  private renderAdd = () => {
    const { context, shelterBranches } = this.props
    const { showRegisterPopover } = this.state

    const selectedBranches = context.qrCodes?.map(v => v.branch?.uuid)
    const branches = shelterBranches.filter(v => !selectedBranches?.includes(v.uuid))

    return (
      <Popover
        title="Create QR Code"
        content={(
          showRegisterPopover && (
            <RegisterQrCode
              context={context}
              shelterBranches={branches}
              onFinished={(err) => this.handlePopoverVisibleChange(!!err, undefined, true)}
            />
          )
        )}
        trigger="click"
        visible={showRegisterPopover}
        onVisibleChange={this.handlePopoverVisibleChange}
      >
        <Button
          type="dashed"
          block
        >
          CREATE NEW QR CODE
        </Button>
      </Popover>
    )
  }

  render() {
    const { shelterBranches, context } = this.props
    const { loaded, showEditPopover } = this.state

    if (!loaded || (context.fetching && !context.qrCodes)) {
      return <Skeleton />
    }

    if (!context.fetching && context.qrCodes && context.qrCodes.length === 0) {
      return (
        <>
          <Empty description="No QR Code generated yet"  />
          <Divider />
          {this.renderAdd()}
        </>
      )
    }

    return (
      <div>
        <Row gutter={24}>
          {
            context.qrCodes?.map(v => {
              const showPopover = showEditPopover.includes(v.uuid)
              const filteredBranches = shelterBranches.filter(b => !context.qrCodes?.filter(x => x.uuid !== v.uuid).map(x => x.branch?.uuid)?.includes(b.uuid))

              return (
                <Col key={v.uuid} xs={24} md={12} style={{marginTop: 10}}>
                  <Card
                    style={{ width: 500 }}
                    actions={[
                      <Popover
                        title="Edit QR Code"
                        content={(
                          showPopover && (
                            <RegisterQrCode
                              context={context}
                              shelterBranches={filteredBranches}
                              data={v}
                              onFinished={(err) => this.handlePopoverVisibleChange(!!err, v.uuid, true)}
                            />
                          )
                        )}
                        trigger="click"
                        visible={showPopover}
                        onVisibleChange={(visible) => this.handlePopoverVisibleChange(visible, v.uuid)}
                      >
                        <EditOutlined key="action-edit" />
                      </Popover>,
                      <Popconfirm
                        title="Are you sure about deleting this QR Code?"
                        okButtonProps={{
                          loading: context.fetching || false
                        }}
                        onConfirm={() => this.handleDelete(v)}
                      >
                        <DeleteOutlined key="action-delete" />
                      </Popconfirm>,
                      <PrinterOutlined key="action-print" onClick={() => this.handlePrintOne(v)} />
                    ]}
                  >
                    <Card.Meta
                      avatar={<div id={`qr-code-${v.uuid}`}><QRCode value={path.join(Config.adoptBase, 'qr-code', v.uuid)} /></div>}
                      title={(
                        <div>
                          <b>{v.title}</b>
                          <p>{v.type_key}</p>
                          <p><b>Location:</b> {v.branch?.name}</p>
                        </div>
                      )}
                      description={v.description}
                    />
                  </Card>
                </Col>
              )
            })
          }
        </Row>

        <Divider />
        {this.renderAdd()}
      </div>
    )
  }
}
